'use strict';
/* ==========================================================================
   core-local/college.js — "Race to 270": the Electoral College model (DESIGN.md §4). API contract for the level
   agents: core-local/COLLEGE.md (read that, not this file).

   Pure: no DOM, no engine globals. Runs in node (module.exports: tools/collegecheck.js, tools/contentcheck.js,
   tools/bankcheck.js --register college=…) and in the page (window.College). Registers itself with the question bank
   as `college` when core/bank.js is loaded first (lesson.json scripts order), so a pool / labs.json formula can say
   college.ev('FL', 2020), college.replay(2000).margin('FL'), college.minShare(2024).percent …

   THE HONESTY RULE: every number comes from content/data.json (a copy of docs/topics/data-electoral.json), loaded
   lazily: in the page from Game.content.data (lesson.json preload.content "data"), in node by require. The few facts
   that are not in that table (the 1800 tally, the compact's member list, the contingent-election rules, the statutory
   calendar rules) are written here once, each with its facts row (docs/topics/facts-electoral.md), and
   tools/collegecheck.js re-reads the facts file to prove them. tie() also reads content/mapdata.json (the map's
   label anchors give it a west-to-east order): Game.content.mapdata in the page.

   NONPARTISAN: the hands-on tickets are 'lantern' and 'anchor'. Real names exist only inside replay() results
   (label / name) and ballots1800() (names of 1800, already voiced in the script).
   ========================================================================== */
(function (root) {
  let DATA = null, MAP = null;
  const CACHE = new Map();
  const memo = (k, f) => { if (!CACHE.has(k)) CACHE.set(k, f()); return CACHE.get(k); };

  /* ---------------- data ---------------- */
  function nodeRequire(rel) {
    if (typeof require !== 'function' || typeof __dirname !== 'string') return null;
    try { return require(require('path').join(__dirname, '..', rel)); } catch (e) { return null; }
  }
  function data() {
    if (DATA) return DATA;
    const G = root.Game, c = G && G.content;
    const raw = (c && c.data && c.data.apportionment) ? c.data : nodeRequire('content/data.json');
    if (!raw) throw new Error('college: no data (Game.content.data in the page, content/data.json in node)');
    return (DATA = index(raw));
  }
  function mapdata() {
    if (MAP) return MAP;
    const G = root.Game, c = G && G.content;
    const raw = (c && c.mapdata && c.mapdata.states) ? c.mapdata : nodeRequire('content/mapdata.json');
    if (!raw) throw new Error('college: no map data (Game.content.mapdata in the page, content/mapdata.json in node)');
    return (MAP = raw);
  }
  /* tests / the page may hand the tables in directly */
  function setData(d) { DATA = d ? index(d) : null; CACHE.clear(); return College; }
  function setMap(m) { MAP = m || null; CACHE.clear(); return College; }

  function index(d) {
    const rows = d.apportionment.states, S = {};
    for (const r of rows) S[r.postal] = r;
    const E = {};
    for (const [y, e] of Object.entries(d.elections || {})) {
      const st = {};
      for (const r of e.states) st[r.postal] = r;
      E[y] = { raw: e, st };
    }
    return { raw: d, rows, S, E, codes: rows.map(r => r.postal) };
  }

  /* ---------------- states and the census ---------------- */
  const CENSUSES = [2010, 2020];
  function cen(c) {
    if (c === undefined || c === null || c === '') return 2020;
    c = +c;
    if (!CENSUSES.includes(c)) throw new Error(`college: census must be 2010 or 2020 (got ${c}); for an election year use censusFor(year)`);
    return c;
  }
  /* the census whose seats an election used (2012-2020 → 2010; 2024-2028 → 2020); 2000 used the 1990 census → null */
  function censusFor(year) { year = +year; if (year >= 2024 && year <= 2028) return 2020; if (year >= 2012 && year <= 2020) return 2010; return null; }
  function row(st) {
    const r = data().S[String(st).toUpperCase()];
    if (!r) throw new Error(`college: unknown state "${st}"`);
    return r;
  }
  const codes = () => data().codes.slice();                       // 51: the 50 states + DC, alphabetical by postal
  const states50 = () => data().codes.filter(c => c !== 'DC');
  const name = st => row(st).name;
  const isState = st => String(st).toUpperCase() !== 'DC';
  function ev(st, census) { const c = cen(census), r = row(st); return c === 2020 ? r.ev2024_2028 : r.ev2012_2020; }
  function house(st, census) { const c = cen(census), r = row(st); return c === 2020 ? r.house2020 : r.house2010; }
  const seats = house;
  const senators = st => (isState(st) ? 2 : 0);                  // D.C.: no Senators; its 3 come from the 23rd Amendment
  const residents = st => row(st).resPop2020;                     // 2020 census resident population (D.C. included)
  const appPop = st => row(st).appPop2020;                        // 2020 apportionment population (null for D.C.)
  function total(census) { const c = cen(census); return memo('total' + c, () => codes().reduce((a, s) => a + ev(s, c), 0)); }
  const need = census => Math.floor(total(census) / 2) + 1;       // the smallest majority: 270
  const half = census => total(census) / 2;                       // 269: a tie
  function houseSize(census) { const c = cen(census); return memo('house' + c, () => codes().reduce((a, s) => a + house(s, c), 0)); }
  const senateSize = () => states50().length * 2;                 // 100
  const dcEv = census => ev('DC', census);                        // 3
  /* the 2010 → 2020 changes (facts 3.3): [{postal, name, from, to, change}] sorted by change, then postal */
  function changes() {
    return memo('changes', () => codes().map(s => ({ postal: s, name: name(s), from: ev(s, 2010), to: ev(s, 2020), change: ev(s, 2020) - ev(s, 2010) }))
      .filter(x => x.change).sort((a, b) => b.change - a.change || (a.postal < b.postal ? -1 : 1)));
  }
  /* Florida over time (facts 3.8; data.floridaElectoralVotesHistory): floridaEv(1848) = 3, floridaEv(2024) = 30 */
  function floridaHistory() { return data().raw.floridaElectoralVotesHistory.rows.map(r => ({ elections: r.elections, ev: r.ev })); }
  function floridaEv(year) {
    year = +year;
    for (const r of floridaHistory()) {
      const [a, b] = String(r.elections).split('-').map(Number);
      if (year >= a && year <= (b || a)) return r.ev;
    }
    throw new Error(`college.floridaEv: no row covers ${year}`);
  }

  /* ---------------- apportionment: the Method of Equal Proportions (facts 3.1) ---------------- */
  /* pops: {postal: population} (or "AL:5030053,AK:733391"); seats: the House size. Every state gets one seat; seats
     n+1 … `seats` go one at a time to the highest priority value pop / sqrt(n (n + 1)), n = the seats it holds.
     → {seats: {postal: n}, order: [{k, postal, seat, priority}]}: k = 1 … `seats` (k ≤ #states: the first seats,
     alphabetical, priority null). */
  function huntingtonHill(pops, size) {
    if (typeof pops === 'string') pops = Object.fromEntries(pops.split(',').map(x => x.split(':')).map(([k, v]) => [k.trim(), +v]));
    const cs = Object.keys(pops).filter(k => pops[k] > 0).sort();
    size = size === undefined ? 435 : +size;
    if (!(Number.isInteger(size) && size >= cs.length)) throw new Error(`college.apportion: ${size} seats for ${cs.length} states`);
    const n = {}, order = [];
    cs.forEach((c, i) => { n[c] = 1; order.push({ k: i + 1, postal: c, seat: 1, priority: null }); });
    for (let k = cs.length + 1; k <= size; k++) {
      let best = null, bp = -1;
      for (const c of cs) { const p = pops[c] / Math.sqrt(n[c] * (n[c] + 1)); if (p > bp) { bp = p; best = c; } }
      n[best]++;
      order.push({ k, postal: best, seat: n[best], priority: bp });
    }
    return { seats: n, order };
  }
  /* deal(census): the census deal for the lab. 2020: computed from the apportionment populations (it reproduces the
     Census table: tools/collegecheck.js). 2010: content/data.json has no 2010 apportionment populations, so the seats
     come from the table and `order` is null (computed: false). */
  function deal(census) {
    const c = cen(census);
    return memo('deal' + c, () => {
      if (c === 2020) {
        const pops = {}; for (const s of states50()) pops[s] = appPop(s);
        const r = huntingtonHill(pops, houseSize(2020));
        return { census: c, computed: true, size: houseSize(2020), seats: r.seats, order: r.order, first: states50().length };
      }
      const seatsT = {}; for (const s of states50()) seatsT[s] = house(s, c);
      return { census: c, computed: false, size: houseSize(c), seats: seatsT, order: null, first: states50().length,
        note: 'content/data.json has no 2010 apportionment populations: the 2010 seats are the Census table\'s, not recomputed' };
    });
  }
  /* apportion(2020) → {AL: 7, …} (the computed deal); apportion(pops, seats) → the general method's seats */
  function apportion(a, size) {
    if (a === undefined || typeof a === 'number' || (typeof a === 'string' && /^\d{4}$/.test(a))) return Object.assign({}, deal(a).seats);
    return huntingtonHill(a, size).seats;
  }
  const dealOrder = census => (deal(census).order || []).map(o => Object.assign({}, o));
  /* the k-th seat dealt that went to `st` (1-based within the state): seatAt('FL', 28) → its k (≤ 435) */
  function seatAt(st, n, census) { const o = (deal(census).order || []).find(x => x.postal === String(st).toUpperCase() && x.seat === +n); return o ? o.k : null; }

  /* ---------------- lists of states ---------------- */
  /* "CA,TX,FL" | ['CA', 'TX'] → ['CA', 'TX'] (upper case, validated) */
  function list(x) {
    if (x === undefined || x === null || x === '') return [];
    const a = Array.isArray(x) ? x : String(x).split(/[\s,]+/).filter(Boolean);
    return a.map(s => { const u = String(s).toUpperCase(); row(u); return u; });
  }
  const sumEv = (x, census) => list(x).reduce((a, s) => a + ev(s, census), 0);
  /* biggest first (ties: more 2020 residents first, then postal; ties never change a sum) */
  function ranked(census) {
    const c = cen(census);
    return memo('ranked' + c, () => codes().slice().sort((a, b) => ev(b, c) - ev(a, c) || residents(b) - residents(a) || (a < b ? -1 : 1)));
  }
  function rankedSmall(census) {
    const c = cen(census);
    return memo('rankedS' + c, () => codes().slice().sort((a, b) => ev(a, c) - ev(b, c) || residents(a) - residents(b) || (a < b ? -1 : 1)));
  }
  const nth = (k, census) => ranked(census)[+k - 1];
  function largest(n, census) { const c = cen(census), s = ranked(c).slice(0, +n); return { n: +n, census: c, states: s, ev: sumEv(s, c), residents: s.reduce((a, x) => a + residents(x), 0) }; }
  function smallest(n, census) { const c = cen(census), s = rankedSmall(c).slice(0, +n); return { n: +n, census: c, states: s, ev: sumEv(s, c), residents: s.reduce((a, x) => a + residents(x), 0) }; }
  /* the fewest states that reach 270 (facts 6.1-6.2): largest-first is optimal for a COUNT of states */
  function fewest(census) {
    const c = cen(census);
    return memo('fewest' + c, () => {
      const R = ranked(c), nd = need(c);
      let k = 0, s = 0;
      while (s < nd) s += ev(R[k++], c);
      return { census: c, count: k, states: R.slice(0, k), ev: s, below: largest(k - 1, c).ev, need: nd };
    });
  }
  /* any set of states: {ev, count, need, reaches, short, extra, minimal (no state can be dropped)} */
  function checkSet(x, census) {
    const c = cen(census), s = [...new Set(list(x))], e = sumEv(s, c), nd = need(c);
    const minimal = e >= nd && s.every(t => e - ev(t, c) < nd);
    return { census: c, states: s, count: s.length, ev: e, need: nd, reaches: e >= nd, short: Math.max(0, nd - e), extra: Math.max(0, e - nd), minimal };
  }

  /* ---------------- the tally (two invented tickets) ---------------- */
  const SIDES = ['lantern', 'anchor'];
  function side(v) {
    if (v === null || v === undefined || v === '' || v === 0) return null;
    const t = String(v).toLowerCase();
    if (t === 'lantern' || t === 'l' || t === '1') return 'lantern';
    if (t === 'anchor' || t === 'a' || t === '2') return 'anchor';
    return null;
  }
  /* assignments: {FL: 'lantern', TX: 'anchor', …} | "CA,TX,FL" (all Lantern) | "CA,TX/NY,FL" (Lantern / Anchor) */
  function assign(a) {
    const out = {};
    if (a && typeof a === 'object' && !Array.isArray(a)) { for (const [k, v] of Object.entries(a)) { const sd = side(v); if (sd) out[list(k)[0]] = sd; } return out; }
    if (Array.isArray(a)) { list(a).forEach(s => (out[s] = 'lantern')); return out; }
    const [l, an] = String(a || '').split('/');
    list(l).forEach(s => (out[s] = 'lantern'));
    list(an).forEach(s => (out[s] = 'anchor'));
    return out;
  }
  function tally(a, census) {
    const c = cen(census), A = assign(a), nd = need(c), t = { lantern: 0, anchor: 0 }, st = { lantern: [], anchor: [], open: [] };
    for (const s of codes()) { const sd = A[s]; if (sd) { t[sd] += ev(s, c); st[sd].push(s); } else st.open.push(s); }
    const open = total(c) - t.lantern - t.anchor;
    const winner = t.lantern >= nd ? 'lantern' : t.anchor >= nd ? 'anchor' : null;
    return { census: c, lantern: t.lantern, anchor: t.anchor, open, total: total(c), need: nd, winner,
      tie: t.lantern === half(c) && t.anchor === half(c),
      left: { lantern: Math.max(0, nd - t.lantern), anchor: Math.max(0, nd - t.anchor) },
      states: st, count: { lantern: st.lantern.length, anchor: st.anchor.length, open: st.open.length } };
  }

  /* ---------------- the sandbox election (DESIGN §5.2 stop 3) ---------------- */
  /* Voters per state = that state's real total votes cast in `year` (default 2024). The student gives each state a
     winner and a MARGIN in percentage points of that state's votes. Every voter picks one of the two tickets (a
     sandbox assumption, stated on the card). margin 0 = the smallest possible win (half the votes + 1: a margin of
     1 or 2 votes, by the parity of the state's total); 100 = every vote. Integer votes, exact:
        winner = max(floor(T (100 + p) / 200), floor(T / 2) + 1), loser = T − winner.
     MARGINS is the slider's notches (the level may offer any whole p in 0..100). */
  const MARGINS = [0, 5, 20, 40, 100];
  const MARGIN_LABEL = { 0: 'by a hair (half + 1)', 5: 'by 5 points', 20: 'by 20 points', 40: 'by 40 points', 100: 'every vote' };
  function electionRow(st, year) {
    const E = data().E[String(year)];
    if (!E) throw new Error(`college: no election table for ${year}`);
    const r = E.st[String(st).toUpperCase()];
    if (!r) throw new Error(`college: no ${year} row for "${st}"`);
    return r;
  }
  const voters = (st, year) => electionRow(st, year === undefined ? 2024 : year).totalVotes;
  function votesFor(T, p) {
    p = Math.max(0, Math.min(100, Math.round(+p || 0)));
    const small = Math.floor(T / 2) + 1;
    const w = p >= 100 ? T : Math.max(Math.floor(T * (100 + p) / 200), small);
    return { winner: w, loser: T - w, margin: w - (T - w), p };
  }
  /* margins: {FL: 5, …} | "FL:5,TX:20" | one number for every state (default 0) */
  function margins(m) {
    if (m === undefined || m === null || m === '') return () => 0;
    if (typeof m === 'number') return () => m;
    if (typeof m === 'object') { const o = {}; for (const [k, v] of Object.entries(m)) o[String(k).toUpperCase()] = +v; return s => (s in o ? o[s] : 0); }
    const o = {}; String(m).split(',').filter(Boolean).forEach(x => { const [k, v] = x.split(':'); o[k.trim().toUpperCase()] = +v; });
    return s => (s in o ? o[s] : 0);
  }
  function stateVotes(st, winner, p, year) {
    const T = voters(st, year), v = votesFor(T, p), sd = side(winner);
    if (!sd) return { postal: String(st).toUpperCase(), total: T, lantern: 0, anchor: 0, margin: 0, p: v.p, winner: null };
    return { postal: String(st).toUpperCase(), total: T, lantern: sd === 'lantern' ? v.winner : v.loser, anchor: sd === 'anchor' ? v.winner : v.loser, margin: v.margin, p: v.p, winner: sd };
  }
  const pct2 = (a, b) => (b ? Math.round(a / b * 10000) / 100 : 0);
  function popular(a, m, year) {
    const y = year === undefined ? 2024 : +year, A = assign(a), M = margins(m), census = censusFor(y) || 2020;
    let L = 0, An = 0, counted = 0, unassigned = 0;
    const rows = [];
    for (const s of codes()) {
      const T = voters(s, y);
      if (!A[s]) { unassigned += T; continue; }
      const r = stateVotes(s, A[s], M(s), y);
      L += r.lantern; An += r.anchor; counted += T; rows.push(r);
    }
    const ev = tally(A, census), leader = L > An ? 'lantern' : An > L ? 'anchor' : (counted ? 'tie' : null);
    return { year: y, census, lantern: L, anchor: An, counted, unassigned, national: L + An + unassigned,
      lanternShare: counted ? L / counted : 0, anchorShare: counted ? An / counted : 0, lanternPct: pct2(L, counted), anchorPct: pct2(An, counted),
      leader, ev, states: rows,
      fewerVotesWins: !!(ev.winner && leader && leader !== 'tie' && leader !== ev.winner) };
  }
  const SANDBOX_ASSUMPTIONS = [
    'Two invented tickets. Every voter picks one of them.',
    'Each state has as many voters as really voted there in {year}.',
    'Electoral votes from the {census} census.',
    'Maine and Nebraska are winner-take-all here. Really, they use districts.',
  ];
  const sandboxAssumptions = year => SANDBOX_ASSUMPTIONS.map(s => s.replace('{year}', String(year === undefined ? 2024 : year)).replace('{census}', String(censusFor(year === undefined ? 2024 : year) || 2020)));

  /* minShare(year): the smallest share of the votes that could win 270 in theory (facts 6.3): two candidates; each
     state's turnout = its real total votes in `year`; win a state with floor(T/2) + 1 votes and get 0 elsewhere; Maine
     and Nebraska winner-take-all; the electoral votes in force that year. 0/1 knapsack, exact DP over 0 … 538. */
  function minShare(year) {
    const y = +year, c = censusFor(y);
    if (!c) throw new Error(`college.minShare: no electoral-vote table for ${year} (2016, 2020, 2024)`);
    return memo('min' + y, () => {
      const cs = codes(), W = total(c), INF = Infinity;
      const cost = cs.map(s => Math.floor(voters(s, y) / 2) + 1), w = cs.map(s => ev(s, c));
      let dp = new Array(W + 1).fill(INF); dp[0] = 0;
      const take = cs.map(() => new Uint8Array(W + 1));
      cs.forEach((s, i) => {
        const nx = dp.slice();
        for (let e = 0; e + w[i] <= W; e++) if (dp[e] < INF && dp[e] + cost[i] < nx[e + w[i]]) { nx[e + w[i]] = dp[e] + cost[i]; take[i][e + w[i]] = 1; }
        dp = nx;
      });
      const nd = need(c);
      let best = nd; for (let e = nd; e <= W; e++) if (dp[e] < dp[best]) best = e;
      const won = []; let e = best;
      for (let i = cs.length - 1; i >= 0; i--) if (take[i][e]) { won.push(cs[i]); e -= w[i]; }
      won.reverse();
      const nat = data().E[String(y)].raw.national.totalVotes, votes = dp[best];
      return { year: y, census: c, votes, total: nat, share: votes / nat, percent: Math.round(votes / nat * 10000) / 100,
        ev: best, count: won.length, states: won,
        assumptions: [
          'Only two tickets, and every voter picks one of them.',
          `Each state has as many voters as really voted there in ${y}.`,
          'The winner takes each of its states by the smallest majority: half the votes, plus one.',
          'The winner gets no votes at all in every other state.',
          'Maine and Nebraska are winner-take-all here. Really, they use districts.',
          `Electoral votes from the ${c} census.`,
        ],
        caption: 'In theory; no election has come near.' };
    });
  }

  /* ---------------- replays: 2000, 2016, 2020, 2024 (FEC tables) ---------------- */
  const shortName = label => String(label).replace(/\s*\([^)]*\)\s*$/, '');
  function replay(year) {
    const y = String(year), E = data().E[y];
    if (!E) throw new Error(`college.replay: no table for ${year} (2000, 2016, 2020, 2024)`);
    return memo('replay' + y, () => {
      const e = E.raw, keys = Object.keys(e.candidates), nat = e.national;
      const [wk, ok] = nat.ev[keys[0]] >= nat.ev[keys[1]] ? [keys[0], keys[1]] : [keys[1], keys[0]];
      const ticket = (k, ink) => ({ key: k, label: e.candidates[k], name: shortName(e.candidates[k]), ink, ev: nat.ev[k], votes: nat.votes[k],
        share: nat.votes[k] / nat.totalVotes, pct: pct2(nat.votes[k], nat.totalVotes) });
      const winner = ticket(wk, 'indigo'), other = ticket(ok, 'ochre');
      const extra = [];
      const states = e.states.map(r => {
        const W = r.votes[wk], O = r.votes[ok], m = Math.abs(W - O);
        const x = r.evOther ? Object.entries(r.evOther).map(([who, n]) => ({ postal: r.postal, who, ev: n, kind: who === 'abstained' ? 'abstained' : 'faithless' })) : [];
        extra.push(...x);
        return { postal: r.postal, name: name(r.postal), votes: { winner: W, other: O, others: r.otherVotes, total: r.totalVotes },
          ev: { winner: r.ev[wk], other: r.ev[ok], extra: x.reduce((a, z) => a + z.ev, 0) },
          carried: W > O ? 'winner' : 'other', margin: m, marginPct: m / r.totalVotes * 100,
          split: r.ev[wk] > 0 && r.ev[ok] > 0, extraVotes: x, note: r.note || null };
      });
      const S = {}; states.forEach(s => (S[s.postal] = s));
      const byClose = states.slice().sort((a, b) => a.marginPct - b.marginPct || (a.postal < b.postal ? -1 : 1));
      const gap = other.votes - winner.votes;
      return {
        year: +y, census: censusFor(+y), allocation: censusFor(+y) ? `${censusFor(+y)} census` : '1990 census',
        winner, other, total: nat.totalVotes, otherVotes: nat.otherVotes, evTotal: nat.evTotal, need: nat.evToWin,
        extra, faithless: extra.filter(x => x.kind === 'faithless').reduce((a, x) => a + x.ev, 0),
        abstained: extra.filter(x => x.kind === 'abstained').reduce((a, x) => a + x.ev, 0),
        popularGap: gap, popularLeader: gap > 0 ? 'other' : 'winner', fewerVotesWon: gap > 0,
        states, state: st => S[String(st).toUpperCase()] || null,
        margin: st => { const s = S[String(st).toUpperCase()]; if (!s) throw new Error(`replay(${y}).margin: no "${st}"`); return s.margin; },
        marginPct: st => S[String(st).toUpperCase()].marginPct,
        closest: n => byClose.slice(0, n === undefined ? 5 : +n).map(s => ({ postal: s.postal, name: s.name, margin: s.margin, marginPct: s.marginPct, carried: s.carried })),
        closestState: () => byClose[0].postal,
        splits: states.filter(s => s.split).map(s => s.postal),
        carried: who => states.filter(s => s.carried === who).map(s => s.postal),
        inkOf: st => { const s = S[String(st).toUpperCase()]; return s.ev.winner >= s.ev.other ? 'indigo' : 'ochre'; },
      };
    });
  }
  /* 1824 / 1876 / 1888: national totals only (facts 4.6-4.8; data.electionsHistoricNational): years where the
     electoral-vote majority winner had fewer popular votes than the main opponent, and years with no majority */
  function fewerVotesWins() {
    return memo('fvw', () => {
      const d = data().raw, years = [], house = [], checked = [];
      for (const [y, h] of Object.entries(d.electionsHistoricNational || {})) {
        if (!/^\d{4}$/.test(y)) continue;
        checked.push(+y);
        const ks = Object.keys(h.ev).sort((a, b) => h.ev[b] - h.ev[a]), w = ks[0];
        if (h.ev[w] < h.evToWin) { house.push(+y); continue; }
        const rival = ks[1];
        if (h.popular && h.popular[w] < h.popular[rival]) years.push(+y);
      }
      for (const y of Object.keys(d.elections)) { checked.push(+y); if (replay(y).fewerVotesWon) years.push(+y); }
      years.sort(); house.sort(); checked.sort();
      return { years, count: years.length, house, checked };
    });
  }

  /* ---------------- winner-take-all and the district method (facts 2.10-2.13) ---------------- */
  const DISTRICT_STATES = ['ME', 'NE'];                           // facts 2.11 (21-A §802), 2.12 (§32-714): law as of 2026-10-01
  function winnerTakeAll() { const s = states50().filter(x => !DISTRICT_STATES.includes(x)); return { states: s.length, plusDC: true, list: s, district: DISTRICT_STATES.slice() }; }
  /* districtSplit('NE', 2024): 2 at large + 1 per district; the result as cast, by ink (replay winner = indigo) */
  function districtSplit(st, year) {
    const s = String(st).toUpperCase();
    if (!DISTRICT_STATES.includes(s)) throw new Error(`college.districtSplit: ${st} is winner-take-all`);
    const y = year === undefined ? 2024 : +year, c = censusFor(y) || 2020, r = replay(y).state(s);
    const atLarge = 2, districts = house(s, c);
    const big = Math.max(r.ev.winner, r.ev.other), small = Math.min(r.ev.winner, r.ev.other);
    return { postal: s, year: y, census: c, ev: ev(s, c), atLarge, districts, perDistrict: 1, big, small, split: small > 0,
      statewide: r.carried, result: { winner: r.ev.winner, other: r.ev.other } };
  }

  /* ---------------- people per elector (facts 6.4-6.7) ---------------- */
  function perElector(st, census) {
    const c = cen(census);
    if (c !== 2020) throw new Error('college.perElector: residents are from the 2020 census, so the 2020-census electoral votes (2024-2028)');
    const r = row(st), e = ev(st, c), h = house(st, c);
    return { postal: r.postal, name: r.name, census: c, residents: Math.round(r.resPop2020 / e), resPop: r.resPop2020, ev: e, house: h,
      perHouseSeat: h ? Math.round(r.appPop2020 / h) : null, perEvApportionment: r.appPop2020 ? Math.round(r.appPop2020 / e) : null,
      senateEv: senators(st), exact: r.resPop2020 / e };
  }
  const allResidents = () => memo('allres', () => codes().reduce((a, s) => a + residents(s), 0));
  const perElectorAverage = () => Math.round(allResidents() / total(2020));
  const perElectorRatio = (a, b) => Math.round(perElector(a).exact / perElector(b).exact * 100) / 100;
  const perHouseSeatRatio = (a, b) => Math.round(appPop(a) / house(a, 2020) / (appPop(b) / house(b, 2020)) * 10) / 10;
  function perElectorRanked() { return memo('perRank', () => codes().map(s => perElector(s)).sort((a, b) => a.exact - b.exact)); }
  /* shares (facts 6.5): percent of all 2020 residents vs percent of the 538 electoral votes, to hundredths */
  function shares(st) { return { postal: String(st).toUpperCase(), residentsPct: pct2(residents(st), allResidents()), evPct: pct2(ev(st, 2020), total(2020)) }; }
  const senateEvPct = () => Math.round(senateSize() / total(2020) * 1000) / 10;   // 18.6

  /* ---------------- the calendar (facts 2.15-2.16: 3 U.S.C. §§ 5, 7, 15, 21; 20th Amendment) ---------------- */
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const D = (y, m, d) => new Date(Date.UTC(y, m, d));
  const firstWd = (y, m, wd) => { const f = D(y, m, 1); return D(y, m, 1 + ((wd - f.getUTCDay() + 7) % 7)); };
  const plus = (dt, n) => new Date(dt.getTime() + n * 86400000);
  function day(dt, id, what) {
    const y = dt.getUTCFullYear(), m = dt.getUTCMonth(), d = dt.getUTCDate();
    return { id, what, iso: dt.toISOString().slice(0, 10), year: y, month: m + 1, monthName: MONTHS[m], mon: MONTHS[m].slice(0, 3), day: d,
      weekday: DAYS[dt.getUTCDay()], shown: `${MONTHS[m].slice(0, 3)} ${d}, ${y}`, long: `${DAYS[dt.getUTCDay()]}, ${MONTHS[m]} ${d}, ${y}` };
  }
  function timeline(year) {
    const y = +year;
    if (!(Number.isInteger(y) && y % 4 === 0 && y >= 2024)) throw new Error(`college.timeline: a presidential election year from 2024 (got ${year}): the Electoral Count Reform Act of 2022 rules`);
    return memo('tl' + y, () => {
      const election = plus(firstWd(y, 10, 1), 1);                     // "the Tuesday next after the first Monday in November" (§21)
      const electors = plus(plus(firstWd(y, 11, 3), 7), 6);            // "the first Tuesday after the second Wednesday in December" (§7)
      const certificates = plus(electors, -6);                         // "not later than … 6 days before" the meeting (§5)
      const votesDue = plus(firstWd(y, 11, 3), 21);                    // the fourth Wednesday in December (NARA)
      const count = D(y + 1, 0, 6), inauguration = D(y + 1, 0, 20);    // §15; 20th Amendment §1 (noon)
      const t = {
        year: y,
        election: day(election, 'election', 'Election Day: voters choose their state\'s electors'),
        certificates: day(certificates, 'certificates', 'Each governor certifies the state\'s electors'),
        electors: day(electors, 'electors', 'The electors meet and vote in each state'),
        votesDue: day(votesDue, 'votesDue', 'The electors\' votes reach Congress and the Archivist'),
        count: day(count, 'count', 'Congress counts the electoral votes'),
        inauguration: day(inauguration, 'inauguration', 'The President takes office at noon'),
      };
      t.pages = [t.election, t.electors, t.count, t.inauguration];       // the four calendar pages, in order
      t.all = [t.election, t.certificates, t.electors, t.votesDue, t.count, t.inauguration];
      t.days = Math.round((inauguration - election) / 86400000);        // Election Day → Inauguration Day
      t.weeks = Math.round(t.days / 7 * 10) / 10;
      return t;
    });
  }

  /* ---------------- no majority: the House, one vote per state (facts 2.6, 2.7, 2.21) ---------------- */
  const CONTINGENT = { votesPer: 1, from: 3, fromBefore: 5, vpBy: 'Senate', vpFrom: 2, rows: '2.6, 2.7, 2.21' };
  function contingent(delegations) {
    const n = states50().length, nd = Math.floor(n / 2) + 1;
    const out = Object.assign({ states: n, need: nd }, CONTINGENT);
    if (delegations !== undefined) {
      const A = assign(delegations); delete A.DC;
      const l = Object.values(A).filter(v => v === 'lantern').length, a = Object.values(A).filter(v => v === 'anchor').length;
      Object.assign(out, { lantern: l, anchor: a, undecided: n - l - a, winner: l >= nd ? 'lantern' : a >= nd ? 'anchor' : null });
    }
    return out;
  }

  /* tie(): one real-numbers 269–269 split of the 2020-census map, every state whole (Maine and Nebraska counted as
     winner-take-all: an assumption the card states). West to east by the map's label anchors (content/mapdata.json):
     Lantern takes the longest western run whose votes stay ≤ 269, then the first subset (in west-to-east order) of
     the next states that makes exactly 269; the rest are Anchor. Deterministic. */
  function tie() {
    return memo('tie', () => {
      const M = mapdata(), c = 2020, H = half(c);
      const order = codes().slice().sort((a, b) => M.states[a].anchor[0] - M.states[b].anchor[0] || (a < b ? -1 : 1));
      let k = 0, s = 0;
      while (k < order.length && s + ev(order[k], c) <= H) s += ev(order[k++], c);
      const d = H - s;
      let pick = d === 0 ? [] : null;
      // the subset of the next states (a window of up to 12, west to east) that sums to d and is earliest in order:
      // compare index lists position by position
      const earlier = (a, b) => { for (let i = 0; i < Math.min(a.length, b.length); i++) if (a[i] !== b[i]) return a[i] < b[i]; return a.length < b.length; };
      for (let win = 1; win <= Math.min(12, order.length - k) && !pick; win++) {
        const cand = order.slice(k, k + win), n = cand.length; let best = null;
        for (let mask = 1; mask < (1 << n); mask++) {
          let t = 0; for (let i = 0; i < n; i++) if (mask & (1 << i)) t += ev(cand[i], c);
          if (t !== d) continue;
          const key = []; for (let i = 0; i < n; i++) if (mask & (1 << i)) key.push(i);
          if (!best || earlier(key, best)) best = key;
        }
        if (best) pick = best.map(i => cand[i]);
      }
      if (!pick) throw new Error('college.tie: no split found');
      const lantern = order.slice(0, k).concat(pick), anchor = order.filter(x => !lantern.includes(x));
      const asg = {}; lantern.forEach(x => (asg[x] = 'lantern')); anchor.forEach(x => (asg[x] = 'anchor'));
      const t = tally(asg, c);
      return { census: c, lantern: t.lantern, anchor: t.anchor, need: need(c), states: { lantern: lantern.slice().sort(), anchor: anchor.slice().sort() },
        assign: asg, count: { lantern: lantern.length, anchor: anchor.length },
        assumption: 'Maine and Nebraska are counted as whole states here (winner-take-all).' };
    });
  }

  /* ---------------- 1800 (facts 5.1; the rule: 2.6; the fix: 2.7) ---------------- */
  const TALLY_1800 = { row: '5.1', electors: 138, votes: [['Thomas Jefferson', 73], ['Aaron Burr', 73], ['John Adams', 65], ['Charles C. Pinckney', 64], ['John Jay', 1]],
    houseBallots: 36, house: { first: 10, second: 4, none: 2 }, tickets: [['Thomas Jefferson', 'Aaron Burr'], ['John Adams', 'Charles C. Pinckney']] };
  function ballots1800() {
    return memo('1800', () => {
      const T = TALLY_1800, votes = T.votes.map(([n, k]) => ({ name: n, votes: k })), top = Math.max(...votes.map(v => v.votes));
      const majority = Math.floor(T.electors / 2) + 1, cast = votes.reduce((a, v) => a + v.votes, 0);
      const tied = votes.filter(v => v.votes === top).map(v => v.name);
      // the same electors under separate ballots (12th Amendment): each elector's presidential vote goes to the top of
      // the ticket that elector's two votes came from; the other vote is the vice-presidential one
      const tk = T.tickets, pres = {}, vp = {};
      const tops = new Set(tk.map(t => t[0]));
      for (const v of votes) { if (tops.has(v.name)) pres[v.name] = v.votes; else vp[v.name] = v.votes; }
      const presList = Object.entries(pres).map(([n, k]) => ({ name: n, votes: k })).sort((a, b) => b.votes - a.votes);
      const vpList = Object.entries(vp).map(([n, k]) => ({ name: n, votes: k })).sort((a, b) => b.votes - a.votes);
      return { year: 1800, row: T.row, electors: T.electors, votesEach: 2, cast, votes, top, tie: tied.length > 1, tied, majority,
        houseBallots: T.houseBallots, house: Object.assign({ states: T.house.first + T.house.second + T.house.none }, T.house),
        separate: { president: presList, vicePresident: vpList, presidentVotes: presList.reduce((a, v) => a + v.votes, 0), majority,
          president1: presList[0], tie: presList.length > 1 && presList[0].votes === presList[1].votes, elected: presList[0].votes >= majority,
          assumption: 'Each elector\'s vote for President goes to the first name on that elector\'s ticket; the other vote is for Vice President.' } };
    });
  }

  /* ---------------- how it can change (facts 2.22) ---------------- */
  const COMPACT = { row: '2.22', members: ['CA', 'CO', 'CT', 'DE', 'DC', 'HI', 'IL', 'ME', 'MD', 'MA', 'MN', 'NJ', 'NM', 'NY', 'OR', 'RI', 'VT', 'VA', 'WA'],
    asOf: 'April 2026', asOfIso: '2026-04-14', checked: '2026-10-01' };
  function compact() {
    const m = COMPACT.members, e = sumEv(m, 2020), nd = need(2020);
    return { row: COMPACT.row, members: m.slice(), states: m.filter(isState).length, plusDC: m.includes('DC'), jurisdictions: m.length, ev: e, need: nd, short: nd - e,
      inEffect: e >= nd, asOf: COMPACT.asOf, asOfIso: COMPACT.asOfIso, checked: COMPACT.checked, label: 'a proposal; not in effect' };
  }
  /* Article V (facts 1.8, 2.22): three-fourths of the states ratify an amendment */
  function ratifyNeed() { const n = states50().length; return { states: n, need: Math.ceil(n * 3 / 4), exact: n * 3 / 4 }; }

  /* ---------------- pool helpers (core/bank.js) ---------------- */
  function itemSteps(item, params) {
    const B = root.Bank;
    if (!B) throw new Error('college.itemSteps: core/bank.js is not loaded');
    const S = item.steps || [];
    return S.map((s, i) => {
      const o = { i, type: s.type, text: B.fill(s.text || '', params), hint: s.hint ? B.fill(s.hint, params) : '', format: s.format || null, last: i === S.length - 1 };
      if (s.type === 'numpad') { o.value = B.eval(String(s.formula), params); o.answerText = B.format(o.value, s.format || 'num'); }
      else if (s.type === 'choice') { o.options = (s.options || []).map(x => B.fill(String(x), params)); o.correct = s.correct; o.answerText = o.options[o.correct]; }
      return o;
    });
  }

  const College = {
    // data
    setData, setMap, data: () => data().raw, mapdata,
    // states, census
    CENSUSES, codes, states50, name, isState, ev, house, seats, senators, residents, appPop, total, need, half, houseSize, senateSize, dcEv,
    censusFor, changes, floridaHistory, floridaEv,
    // apportionment
    apportion, deal, dealOrder, seatAt, huntingtonHill,
    // lists
    list, sumEv, ranked, nth, largest, smallest, fewest, checkSet,
    // tally + sandbox
    SIDES, side, assign, tally, MARGINS, MARGIN_LABEL, voters, votesFor, stateVotes, popular, sandboxAssumptions, minShare, pct2,
    // real elections
    replay, fewerVotesWins, DISTRICT_STATES, winnerTakeAll, districtSplit,
    // people per elector
    perElector, perElectorAverage, perElectorRatio, perHouseSeatRatio, perElectorRanked, shares, senateEvPct, allResidents,
    // calendar, contingent, tie, 1800, change
    timeline, contingent, tie, ballots1800, compact, ratifyNeed,
    itemSteps,
  };
  root.College = College;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('college', College);
  if (typeof module !== 'undefined' && module.exports) module.exports = College;
})(typeof window !== 'undefined' ? window : globalThis);
